import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { apiService } from '../services/api';

const BOT_NAME = 'Boldcomp AI';

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi — I can point you to services, courses, or a free project scope. What are you trying to fix: software, AI, automation, or training?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      return window.sessionStorage.getItem('chat_sid') || '';
    } catch { return ''; }
  });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const res = await apiService.chat(text, sessionId || undefined);
      if (res?.session_id && !sessionId) {
        setSessionId(res.session_id);
        try { window.sessionStorage.setItem('chat_sid', res.session_id); } catch (_) {}
      }
      setMessages(prev => [...prev, { role: 'assistant', content: res?.reply || "Sorry, I couldn't process that. Please try again or contact us directly." }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: "Connection error. Please try again or use the Contact page." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Fixed bottom-right; z-index above ConversionLeadBar */}
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 12, sm: 20 },
          left: 'auto',
          top: 'auto',
          bottom: { xs: 12, sm: 16 },
          zIndex: 10020,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        {open && (
          <Box
            sx={{
              width: 360,
              maxWidth: 'min(360px, calc(100vw - 24px))',
              height: 420,
              maxHeight: 'min(420px, calc(100vh - 100px))',
              background: 'white',
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              mb: 1.5,
              alignSelf: 'flex-end',
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1D1D4E 0%, #1a237e 100%)',
                color: 'white',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToyOutlinedIcon sx={{ fontSize: 22, opacity: 0.95 }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  {BOT_NAME}
                </Typography>
              </Box>
              <IconButton size="small" sx={{ color: 'white' }} onClick={() => setOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {messages.map((m, i) => (
                <Box
                  key={i}
                  sx={{
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: m.role === 'user' ? 'primary.main' : 'grey.100',
                    color: m.role === 'user' ? 'white' : 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {m.content}
                  </Typography>
                </Box>
              ))}
              {loading && (
                <Box sx={{ alignSelf: 'flex-start', px: 2, py: 1.25, borderRadius: 2, bgcolor: 'grey.100' }}>
                  <Typography variant="body2" color="text.secondary">
                    Typing...
                  </Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Type a message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKey}
                disabled={loading}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <IconButton
                color="primary"
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&:disabled': { bgcolor: 'grey.300' } }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        )}
        <IconButton
          onClick={() => setOpen(!open)}
          className="ai-assistant-fab-pulse"
          aria-label={open ? 'Close AI assistant' : 'Open AI assistant'}
          title="AI assistant"
          sx={{
            width: 58,
            height: 58,
            bgcolor: 'primary.main',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.22)',
            '&:hover': {
              bgcolor: 'primary.dark',
              animation: 'none',
              opacity: 1,
              transform: 'scale(1.05)',
            },
          }}
        >
          <SmartToyOutlinedIcon className="ai-assistant-fab-icon" sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>
    </>
  );
}
